import { applyPrivacy, createCalendarSource, defaultAccountSharing, sourceAcceptsEvent, } from '@trackmind/core';
import { GoogleCalendarAPI } from './google-calendar';
import { CALENDAR_SHARED_FILE, eventsForSharing, mergeOwnedSlices, readSharedFile, resolveSharePrivacy, } from './shared-file';
/**
 * Orchestrates every connected calendar.
 *
 * Refresh is a POLL, not a push. Google can push changes to a public webhook,
 * but a webhook needs a server that stays up and costs money per user, which
 * this app deliberately doesn't have (CLAUDE.md §6). Instead we poll with
 * incremental sync tokens on the same triggers the data sync already uses:
 * app start, tab becoming visible again, and a timer while the app is open.
 * After the first refresh each poll is a near-empty response per calendar, so
 * the cadence can be tight without being expensive.
 *
 * Practical effect: a meeting someone adds to your calendar shows up the next
 * time TrackMind is open and polls — within a minute or so of you looking at
 * it, not the instant it is created.
 */
/** How far back and forward we keep events. */
export const WINDOW_DAYS_BACK = 30;
export const WINDOW_DAYS_FORWARD = 120;
export const CALENDAR_REFRESH_INTERVAL_MS = 5 * 60 * 1000;
/** Don't re-poll more often than this, however many triggers fire. */
const MIN_REFRESH_INTERVAL_MS = 60 * 1000;
function isoDay(offsetDays) {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
}
function dayKey(offsetDays) {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
export class CalendarEngine {
    cache;
    auth;
    remote;
    deviceLabel;
    timer = null;
    lastRefreshAt = 0;
    refreshing = null;
    constructor(config) {
        this.cache = config.cache;
        this.auth = config.auth;
        this.remote = config.remote ?? null;
        this.deviceLabel = config.deviceLabel ?? null;
    }
    /** Sign-in can happen after the engine boots, so this is settable. */
    setRemote(remote) {
        this.remote = remote;
    }
    /** True when this device holds the credentials for an account. */
    owns(accountId) {
        return this.auth.hasToken(accountId);
    }
    apiFor(accountId) {
        return new GoogleCalendarAPI({
            getAccessToken: () => this.auth.getAccessToken(accountId),
        });
    }
    // ── Connecting ──────────────────────────────────────────────────────────
    /**
     * Register a freshly-authorised account and discover its calendars.
     *
     * Re-connecting an account already present updates it in place and keeps
     * the user's per-calendar choices — reconnecting after an expired grant
     * must not silently reset which calendars were enabled.
     */
    async registerAccount(user) {
        const existing = (await this.cache.getAccounts()).find((a) => a.id === user.id);
        const account = {
            id: user.id,
            provider: 'google',
            email: user.email,
            name: user.name,
            avatar_url: user.avatarUrl,
            connected_at: existing?.connected_at ?? new Date().toISOString(),
            needs_reauth: false,
            // New account: share busy times so it works on every device. Existing
            // account: leave exactly as it was. See defaultAccountSharing.
            ...defaultAccountSharing(existing),
        };
        await this.cache.saveAccount(account);
        await this.discoverCalendars(account.id);
        return account;
    }
    /** Sync the calendar list for an account, preserving existing per-calendar settings. */
    async discoverCalendars(accountId) {
        const entries = await this.apiFor(accountId).listCalendars();
        const existing = (await this.cache.getSources()).filter((s) => s.account_id === accountId);
        const byCalendarId = new Map(existing.map((s) => [s.calendar_id, s]));
        const sources = [];
        for (const entry of entries) {
            const previous = byCalendarId.get(entry.id);
            if (previous) {
                // Name and colour can change upstream; the user's choices must not.
                sources.push({
                    ...previous,
                    name: entry.summaryOverride ?? entry.summary,
                    color: entry.backgroundColor ?? previous.color,
                    is_primary: Boolean(entry.primary),
                });
            }
            else {
                sources.push(createCalendarSource({
                    account_id: accountId,
                    calendar_id: entry.id,
                    name: entry.summaryOverride ?? entry.summary,
                    color: entry.backgroundColor ?? null,
                    is_primary: Boolean(entry.primary),
                }));
            }
        }
        await this.cache.saveSources(sources);
        // Calendars the user lost access to (left the company, unsubscribed).
        for (const stale of existing) {
            if (!entries.some((e) => e.id === stale.calendar_id)) {
                await this.cache.deleteSource(stale.id);
            }
        }
        return sources;
    }
    async getAccounts() {
        return this.cache.getAccounts();
    }
    async getSources() {
        return this.cache.getSources();
    }
    /**
     * Change one calendar's settings.
     *
     * Turning a calendar off, or tightening its privacy, drops its cached
     * events immediately rather than waiting for the next refresh — the user
     * asked for that data to stop being here, so it stops being here.
     */
    async updateSource(sourceId, update) {
        const sources = await this.cache.getSources();
        const source = sources.find((s) => s.id === sourceId);
        if (!source)
            throw new Error(`Calendar ${sourceId} not found`);
        const next = { ...source, ...update };
        const wentQuiet = source.enabled && next.enabled === false;
        const privacyTightened = source.privacy === 'full' && next.privacy === 'busy';
        const filtersWidened = (!source.include_declined && next.include_declined) ||
            (!source.include_all_day && next.include_all_day);
        if (wentQuiet || privacyTightened) {
            await this.cache.clearEventsForSource(sourceId);
            next.sync_token = null;
            next.last_synced_at = null;
        }
        else if (filtersWidened) {
            // Previously-skipped events were never cached, and an incremental token
            // would never mention them again. Force a full refetch.
            next.sync_token = null;
        }
        await this.cache.saveSource(next);
        if ((next.enabled && !source.enabled) || filtersWidened) {
            await this.refreshSource(next);
        }
        return next;
    }
    async disconnectAccount(accountId) {
        const wasOwned = this.owns(accountId);
        await this.cache.deleteAccount(accountId);
        this.auth.removeAccount(accountId);
        // If this device was publishing the account, withdraw it from the shared
        // file too — otherwise disconnecting here would leave the data sitting in
        // the user's storage with nothing left to update or remove it.
        if (wasOwned) {
            try {
                await this.publishShared();
            }
            catch (error) {
                console.error('[Calendar] failed to withdraw shared calendar:', error);
            }
        }
    }
    async disconnectAll() {
        await this.cache.clearAll();
        this.auth.removeAll();
    }
    // ── Refresh ─────────────────────────────────────────────────────────────
    /**
     * Poll every enabled calendar. Concurrent calls share one in-flight refresh
     * so the visibility trigger and the interval can't stampede.
     */
    async refresh(options = {}) {
        if (this.refreshing)
            return this.refreshing;
        if (!options.force && Date.now() - this.lastRefreshAt < MIN_REFRESH_INTERVAL_MS) {
            return { refreshedSources: 0, newOrChangedEvents: 0, removedEvents: 0, failedAccounts: [] };
        }
        this.refreshing = this.doRefresh();
        try {
            return await this.refreshing;
        }
        finally {
            this.refreshing = null;
            this.lastRefreshAt = Date.now();
        }
    }
    async doRefresh() {
        const result = {
            refreshedSources: 0,
            newOrChangedEvents: 0,
            removedEvents: 0,
            failedAccounts: [],
        };
        const accounts = await this.cache.getAccounts();
        // NB: no early return on an empty account list. A device that has never
        // connected anything itself can still receive mirrored calendars from the
        // user's other devices — that's the whole point of mirroring, and bailing
        // out here made it invisible on exactly the machine that needed it.
        const accountsById = new Map(accounts.map((a) => [a.id, a]));
        // One dead account must not stop the others from refreshing.
        const deadAccounts = new Set();
        // Re-read the calendar list every poll. Without this, a calendar created
        // (or shared with you) after you connected would never appear — you'd
        // have to disconnect and reconnect the whole account to see it. It's one
        // small request per account, and it also picks up renames and colour
        // changes. Per-calendar settings are preserved by discoverCalendars.
        for (const account of accounts) {
            // A mirrored account has no credentials here by definition. Asking the
            // provider about it would fail and wrongly flag it as needing reauth.
            if (account.mirrored || !this.owns(account.id))
                continue;
            try {
                await this.discoverCalendars(account.id);
            }
            catch (error) {
                const message = error instanceof Error ? error.message : String(error);
                deadAccounts.add(account.id);
                result.failedAccounts.push({ accountId: account.id, email: account.email, error: message });
                await this.cache.saveAccount({ ...account, needs_reauth: true });
            }
        }
        // Mirrored calendars are refreshed by the device that owns them; here
        // they're only read.
        const sources = (await this.cache.getSources()).filter((s) => s.enabled && !s.mirrored);
        for (const source of sources) {
            const account = accountsById.get(source.account_id);
            if (!account || deadAccounts.has(account.id))
                continue;
            try {
                const counts = await this.refreshSource(source);
                result.refreshedSources++;
                result.newOrChangedEvents += counts.written;
                result.removedEvents += counts.removed;
                if (account.needs_reauth) {
                    await this.cache.saveAccount({ ...account, needs_reauth: false });
                    accountsById.set(account.id, { ...account, needs_reauth: false });
                }
            }
            catch (error) {
                const message = error instanceof Error ? error.message : String(error);
                deadAccounts.add(account.id);
                result.failedAccounts.push({ accountId: account.id, email: account.email, error: message });
                await this.cache.saveAccount({ ...account, needs_reauth: true });
            }
        }
        if (accounts.length > 0) {
            await this.cache.pruneOutside(dayKey(-WINDOW_DAYS_BACK), dayKey(WINDOW_DAYS_FORWARD));
        }
        // Opt-in cross-device mirroring. Publishing is best-effort and must never
        // make a calendar refresh look like it failed — the local cache is what
        // the user actually sees.
        try {
            await this.publishShared();
            const mirrored = await this.pullShared();
            result.refreshedSources += mirrored;
        }
        catch (error) {
            console.error('[Calendar] cross-device mirror failed:', error);
        }
        return result;
    }
    /**
     * Write this device's slices of the shared file.
     *
     * Only accounts we hold credentials for AND that the user has explicitly
     * opted in are published; everyone else's slices are left exactly as they
     * were, so several devices can each publish their own accounts into one
     * file without trampling each other.
     */
    async publishShared(authoritativeAccountId) {
        if (!this.remote)
            return;
        const accounts = await this.cache.getAccounts();
        const owned = accounts.filter((a) => !a.mirrored && this.owns(a.id));
        if (owned.length === 0)
            return;
        const sharing = owned.filter((a) => a.share_across_devices);
        const existing = await readSharedFile(this.remote.readRawFile);
        // Couldn't read it: writing now would publish a file built on nothing and
        // wipe every other device's slice. Skip this cycle instead.
        if (!existing)
            return;
        // Nothing to publish and nothing previously published: don't churn the
        // user's repo with a no-op write.
        const hadSlices = owned.some((a) => a.id in existing.accounts);
        if (sharing.length === 0 && !hadSlices)
            return;
        const allSources = await this.cache.getSources();
        const allEvents = await this.cache.getEvents();
        const slices = {};
        for (const account of sharing) {
            const sources = allSources.filter((s) => s.account_id === account.id && s.enabled);
            const sourceIds = new Set(sources.map((s) => s.id));
            // Two filters, in order: the calendar's own privacy already applied on
            // the way into the cache, then the account's SHARE privacy applied here,
            // on the way out. The second is why a work calendar can keep its titles
            // on this machine and still publish nothing but busy time.
            //
            // Which value that is, is settled by the FILE rather than by this
            // device, so two devices holding the same account can't take turns
            // publishing different things (resolveSharePrivacy).
            const privacy = resolveSharePrivacy({
                local: account.share_privacy,
                published: existing.accounts[account.id]?.share_privacy,
                authoritative: account.id === authoritativeAccountId,
            });
            if (privacy !== (account.share_privacy ?? 'busy')) {
                await this.cache.saveAccount({ ...account, share_privacy: privacy });
            }
            const shareTitles = privacy === 'full';
            const events = eventsForSharing(allEvents.filter((e) => sourceIds.has(e.source_id)))
                .map((e) => (shareTitles ? e : applyPrivacy(e, 'busy')));
            slices[account.id] = {
                email: account.email,
                name: account.name,
                updated_at: new Date().toISOString(),
                device: this.deviceLabel,
                sources,
                events,
                share_privacy: privacy,
            };
        }
        const merged = mergeOwnedSlices(existing, owned.map((a) => a.id), slices);
        await this.remote.writeRawFile(CALENDAR_SHARED_FILE, JSON.stringify(merged, null, 2), 'calendar: update shared calendars');
    }
    /**
     * Adopt slices published by the user's other devices.
     *
     * This is what makes a calendar usable on a machine that cannot sign in to
     * the account it belongs to. Local display choices (`enabled`,
     * `blocks_time`) are preserved per device — the owning device decides what
     * exists, this device decides what it wants to see.
     */
    async pullShared() {
        if (!this.remote)
            return 0;
        const file = await readSharedFile(this.remote.readRawFile);
        // Unreadable is not the same as empty. Keep whatever was pulled before —
        // stale mirrored calendars are far better than calendars that disappear
        // whenever a request fails.
        if (!file)
            return 0;
        const localAccounts = await this.cache.getAccounts();
        const localSources = await this.cache.getSources();
        const localById = new Map(localSources.map((s) => [s.id, s]));
        let adopted = 0;
        const seen = new Set();
        for (const [accountId, slice] of Object.entries(file.accounts)) {
            // Never shadow an account this device authorised itself: live data
            // beats a mirror.
            if (this.owns(accountId))
                continue;
            seen.add(accountId);
            const existing = localAccounts.find((a) => a.id === accountId);
            await this.cache.saveAccount({
                id: accountId,
                provider: 'google',
                email: slice.email,
                name: slice.name ?? null,
                avatar_url: null,
                connected_at: existing?.connected_at ?? slice.updated_at,
                needs_reauth: false,
                mirrored: true,
                mirrored_at: slice.updated_at,
            });
            await this.cache.saveSources(slice.sources.map((source) => ({
                ...source,
                mirrored: true,
                // Whether to show it here is a local choice; default to what the
                // owning device had. Same for how it ranks against the other
                // calendars on THIS device when they clash — the work laptop's
                // ordering has no bearing on the personal one's.
                enabled: localById.get(source.id)?.enabled ?? source.enabled,
                blocks_time: localById.get(source.id)?.blocks_time ?? source.blocks_time,
                priority: localById.get(source.id)?.priority ?? source.priority,
                // No credentials here, so no incremental cursor of our own.
                sync_token: null,
                last_synced_at: slice.updated_at,
            })));
            // Replace wholesale: the publisher's slice is authoritative, so an
            // event cancelled on the owning device disappears here too.
            for (const source of slice.sources) {
                await this.cache.clearEventsForSource(source.id);
            }
            await this.cache.saveEvents(slice.events);
            adopted += slice.sources.length;
        }
        // An account whose owner stopped sharing (or was disconnected there) must
        // stop appearing here.
        for (const account of localAccounts) {
            if (account.mirrored && !seen.has(account.id)) {
                await this.cache.deleteAccount(account.id);
            }
        }
        return adopted;
    }
    /** Turn cross-device mirroring on or off for one account. */
    async setAccountSharing(accountId, share, privacy) {
        const account = (await this.cache.getAccounts()).find((a) => a.id === accountId);
        if (!account)
            throw new Error(`Account ${accountId} not found`);
        const next = {
            ...account,
            share_across_devices: share,
            share_privacy: privacy ?? account.share_privacy ?? 'busy',
        };
        await this.cache.saveAccount(next);
        // Publish (or withdraw) straight away — a user who just turned this off
        // expects the data gone from their storage now, not in five minutes.
        // Authoritative: this is the user's own instruction, so it overrides what
        // another device published rather than being adopted from it.
        await this.publishShared(accountId);
        return next;
    }
    async refreshSource(source) {
        const api = this.apiFor(source.account_id);
        const fetched = await api.listEvents({
            calendarId: source.calendar_id,
            sourceId: source.id,
            accountId: source.account_id,
            syncToken: source.sync_token,
            timeMin: isoDay(-WINDOW_DAYS_BACK),
            timeMax: isoDay(WINDOW_DAYS_FORWARD),
        });
        // A full re-list is authoritative: drop what we had rather than merging
        // into it, or events deleted while we weren't looking would survive.
        if (fetched.didFullResync) {
            await this.cache.clearEventsForSource(source.id);
        }
        else {
            await this.cache.deleteEventsByBaseId(fetched.touchedBaseIds);
        }
        // Filtering happens HERE, before anything is written, so an event the
        // user chose not to keep is never persisted even briefly. Same for
        // privacy: a `busy` calendar's titles are dropped on arrival and never
        // touch the disk.
        const keep = [];
        for (const event of fetched.events) {
            if (!sourceAcceptsEvent(source, event))
                continue;
            keep.push(applyPrivacy(event, source.privacy));
        }
        await this.cache.saveEvents(keep);
        await this.cache.saveSource({
            ...source,
            sync_token: fetched.syncToken,
            last_synced_at: new Date().toISOString(),
        });
        return {
            written: keep.length,
            removed: fetched.didFullResync ? 0 : fetched.touchedBaseIds.length - keep.length,
        };
    }
    // ── Reading ─────────────────────────────────────────────────────────────
    async getEventsForDate(date) {
        return this.cache.getEventsForDate(date);
    }
    async getEventsInRange(startDate, endDate) {
        return this.cache.getEventsInRange(startDate, endDate);
    }
    // ── Auto-refresh ────────────────────────────────────────────────────────
    startAutoRefresh(intervalMs = CALENDAR_REFRESH_INTERVAL_MS) {
        if (this.timer)
            return;
        this.timer = setInterval(() => {
            this.refresh().catch((error) => console.error('[Calendar] Auto refresh failed:', error));
        }, intervalMs);
    }
    stopAutoRefresh() {
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }
    }
}
//# sourceMappingURL=engine.js.map